import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DateTime } from 'luxon';

import { useClassName } from 'common/hooks';

const PAGE_SIZE = 12;
const YEARS_BACK = 120;
const MONTH_NAMES = Array.from({ length: 12 }, (_, index) =>
  DateTime.local(2000, index + 1, 1).toFormat('MMM')
);

const getPageEnd = (year, thisYear) =>
  thisYear - Math.floor((thisYear - year) / PAGE_SIZE) * PAGE_SIZE;

const YearMonthPicker = ({ view, date, onChange, onViewChange }) => {
  const classNames = useClassName('Calendar');
  const now = new Date();
  const thisYear = now.getFullYear();
  const year = date.getFullYear();
  const [pageEnd, setPageEnd] = useState(getPageEnd(year, thisYear));
  const oldestYear = thisYear - YEARS_BACK;

  const header = (title, { onPrev, onNext, onTitleClick }) => (
    <div className={classNames('picker-header')}>
      <button
        type="button"
        aria-label="Previous"
        className={classNames('picker-nav', 'prev')}
        disabled={!onPrev}
        onClick={onPrev}
      />
      <button
        type="button"
        className={classNames('picker-title')}
        disabled={!onTitleClick}
        onClick={onTitleClick}>
        {title}
      </button>
      <button
        type="button"
        aria-label="Next"
        className={classNames('picker-nav', 'next')}
        disabled={!onNext}
        onClick={onNext}
      />
    </div>
  );

  if (view === 'years') {
    const years = Array.from(
      { length: PAGE_SIZE },
      (_, index) => pageEnd - PAGE_SIZE + 1 + index
    ).filter((item) => item >= oldestYear);

    return (
      <div className={classNames('picker')}>
        {header(`${years[0]} – ${years[years.length - 1]}`, {
          onPrev:
            years[0] > oldestYear
              ? () => setPageEnd(pageEnd - PAGE_SIZE)
              : undefined,
          onNext:
            pageEnd < thisYear
              ? () => setPageEnd(pageEnd + PAGE_SIZE)
              : undefined,
        })}
        <div className={classNames('picker-grid')}>
          {years.map((item) => (
            <button
              key={item}
              type="button"
              className={classNames(
                'picker-item',
                item === year ? 'selected' : ''
              )}
              onClick={() => {
                const month =
                  item === thisYear
                    ? Math.min(date.getMonth(), now.getMonth())
                    : date.getMonth();
                onChange(new Date(item, month));
                onViewChange('months');
              }}>
              {item}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={classNames('picker')}>
      {header(year, {
        onPrev:
          year > oldestYear
            ? () => onChange(new Date(year - 1, date.getMonth()))
            : undefined,
        onNext:
          year < thisYear
            ? () =>
                onChange(
                  new Date(
                    year + 1,
                    year + 1 === thisYear
                      ? Math.min(date.getMonth(), now.getMonth())
                      : date.getMonth()
                  )
                )
            : undefined,
        onTitleClick: () => {
          setPageEnd(getPageEnd(year, thisYear));
          onViewChange('years');
        },
      })}
      <div className={classNames('picker-grid')}>
        {MONTH_NAMES.map((name, month) => (
          <button
            key={name}
            type="button"
            disabled={year === thisYear && month > now.getMonth()}
            className={classNames(
              'picker-item',
              month === date.getMonth() ? 'selected' : ''
            )}
            onClick={() => {
              onChange(new Date(year, month));
              onViewChange('days');
            }}>
            {name}
          </button>
        ))}
      </div>
    </div>
  );
};

YearMonthPicker.propTypes = {
  view: PropTypes.oneOf(['years', 'months']).isRequired,
  date: PropTypes.instanceOf(Date).isRequired,
  onChange: PropTypes.func.isRequired,
  onViewChange: PropTypes.func.isRequired,
};

export default YearMonthPicker;
